// Lou session (client side). React#158 Inc2c: hold the Lou Tier-1 access token
// (the per-user Salesforce context credential, minted after a step-up) IN MEMORY
// and refresh it silently on a timer from the cookie-authenticated
// /auth/lou/session endpoint, instead of persisting it in a JS-readable cookie.
//
// Only the hardened refresh cookie (HttpOnly/Secure/SameSite=None/Partitioned,
// set by the dbServer Lou callback) lives on disk; the short-lived access token
// never touches storage, so an XSS cannot exfiltrate it, and an expired access
// token is replaced silently without a re-login (the chronic re-prompt symptom
// the 7-day cookie + no-refresh model caused). This is the session model the
// other paths move toward; consumers (the AI agent step-up, editor metadata
// reads) read the token via getLouAccessToken().

import { getLouSession } from "./louStepUp";

// In-memory only (module scope): cleared on a full reload, re-established from
// the refresh cookie via establishLouSession().
let accessToken = null;
let refreshTimer = null;

// Lou access tokens are short-lived (~15 min). Refresh comfortably before expiry
// so a token is always warm without ever letting one expire mid-use.
const REFRESH_INTERVAL_MS = 12 * 60 * 1000;

// The current in-memory Lou access token, or null if no session is established.
export function getLouAccessToken() {
  return accessToken;
}

function scheduleRefresh() {
  if (refreshTimer) {
    clearInterval(refreshTimer);
  }
  refreshTimer = setInterval(async () => {
    const token = await getLouSession();
    if (token) {
      accessToken = token;
    } else {
      // The refresh cookie is gone or expired: drop the token and stop the timer
      // rather than spin. A later step-up re-establishes the session.
      clearLouSession();
    }
  }, REFRESH_INTERVAL_MS);
}

// Establish the in-memory Lou session from the refresh cookie (call after the
// step-up redirect returns, and on app load where a Lou refresh cookie may
// already exist). Returns the access token, or null if there is no Lou session.
export async function establishLouSession() {
  const token = await getLouSession();
  if (!token) {
    return null;
  }
  accessToken = token;
  scheduleRefresh();
  return token;
}

// Clear the in-memory session and stop the refresh timer (logout, or the refresh
// cookie is gone).
export function clearLouSession() {
  accessToken = null;
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
}
